import React from "react";
import {
  X, AlertTriangle, Timer, Phone, PhoneCall, MapPin, User, Star, 
  Settings, UserCheck, Bell, Edit, Users
} from "lucide-react";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/badge";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { GlobalService } from "../../modules";
import { assignProfessionalWhatsApp } from "../../redux/Actions/AuthAction";
import { useAppDispatch } from "../../redux";

interface RequestDetailsModalProps {
  request: any;
  isOpen: boolean;
  onClose: () => void;
  emergencyProfessionals: any[];
  onAssignProvider: (request: any) => void;
  onEditJob: (request: any) => void;
}

const RequestDetailsModal: React.FC<RequestDetailsModalProps> = ({
  request,
  isOpen,
  onClose,
  emergencyProfessionals,
  onAssignProvider,
  onEditJob
}) => {
  const dispatch = useAppDispatch();

  if (!request || !isOpen) return null;

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      unassigned: "bg-red-100 text-red-700",
      provider_assigned: "bg-yellow-100 text-yellow-700",
      en_route: "bg-blue-100 text-blue-700",
      on_site: "bg-green-100 text-green-700",
      approved: "bg-green-100 text-green-700"
    };
    return styles[status] || "bg-gray-100 text-gray-700";
  };

  const getSeverityBadge = (severity: string) => {
    const styles: Record<string, string> = {
      critical: "bg-red-100 text-red-700 border-red-200",
      high: "bg-orange-100 text-orange-700 border-orange-200",
      moderate: "bg-yellow-100 text-yellow-700 border-yellow-200"
    };
    return styles[severity] || "bg-gray-100 text-gray-700";
  };

  const handleWhatsAppAssignment = async (request: any) => {
    console.log(request.description, "request");
    // Check if provider has WhatsApp number
    if (!request.provider?.whatsappNumber) {
      GlobalService.showToast("error", "Error", "Provider does not have a WhatsApp number");
      return;
    }

    try {
      const whatsAppData = {
        to: request.provider.whatsappNumber,
        var1_name: request.provider.name || "Professional",
        var2_jobTitle: request.title || "Emergency Job",
        var3_jobDescription: (request.description || "Emergency service request").replace(/\s*\n\s*/g, " ").trim(),
        var4_clientName: request.customer?.name || "Client",
        var5_clientPhone: request.customer?.phone || "N/A",
        var6_clientWhatsapp: request.customer?.whatsappNumber || "N/A",
        var7_clientEmail: request.customer?.email || "N/A",
        var8_clientAddress: request.customer?.address || "N/A",
        var9_detailsUrl: "https://kokkobeo.com"
      };

      await dispatch(assignProfessionalWhatsApp(whatsAppData));
    } catch (error) {
      console.error("Failed to send WhatsApp message:", error);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" style={{ textAlign: "left" }}>
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b sticky top-0 bg-white">
          <div className="flex justify-between items-start">
            <div>
              <div className="flex items-center gap-3">
                <Badge variant={undefined} className={getStatusBadge(request.status)}>
                  {request.status.replace("_", " ").toUpperCase()}
                </Badge>
                <Badge variant={undefined} className={getSeverityBadge(request.severity)}>
                  {request.severity.toUpperCase()}
                </Badge>
                {request.isFlagged && (
                  <Badge variant="outline" className="border-orange-200 text-orange-700">
                    FLAGGED
                  </Badge>
                )}
              </div>
              <h2 className="text-xl font-semibold mt-2">Emergency {request.type}</h2>
              <p className="text-sm text-gray-500">ID: {request.id}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Emergency Alert */}
          <Alert className="bg-red-50 border-red-200">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-700">
              <span className="font-medium">Emergency Request - </span>
              {request.description}
            </AlertDescription>
          </Alert>

          {/* Response Time */}
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Timer className="h-5 w-5 text-orange-600" />
                <span className="font-medium text-orange-800">Required Response: {request.responseTime}</span>
              </div>
              <Badge variant={undefined} className="bg-orange-100 text-orange-700">
                Wait Time: {request.waitTime}
              </Badge>
            </div>
          </div>

          {/* Customer Information */}
          <div className="space-y-4">
            <h3 className="font-medium">Customer Information</h3>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-sm text-gray-500">Name</span>
                  <p className="font-medium">{request.customer.name}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Phone</span>
                  <div className="flex items-center gap-2">
                    <p className="font-medium">{request.customer.phone}</p>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="h-8"
                      onClick={() => {
                        if (request.customer.phone && request.customer.phone !== "N/A") {
                          window.open(`tel:${request.customer.phone}`, "_self");
                        } else {
                          GlobalService.showToast("error", "Error", "No phone number available for this customer");
                        }
                      }}
                    >
                      <Phone className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Email</span>
                  <p className="font-medium">{request.customer.email}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Address</span>
                  <p className="font-medium">{request.customer.address}</p>
                </div>
              </div>
              <div className="mt-4">
                <span className="text-sm text-gray-500">Location Area</span>
                <div className="flex items-center gap-2 mt-1">
                  <MapPin className="h-4 w-4 text-gray-400" />
                  <span>{request.customer.location.area}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Provider Information */}
          {request.provider ? (
            <div className="space-y-4">
              <h3 className="font-medium">Assigned Provider</h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <User className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-medium">{request.provider.name}</h4>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Star className="h-4 w-4 text-yellow-400 fill-current" />
                        <span>{request.provider.rating}</span>
                        {request.provider.verified && (
                          <Badge variant={undefined} className="bg-green-100 text-green-700">Verified</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="flex items-center gap-2"
                    onClick={() => {
                      if (request.provider.phone && request.provider.phone !== "N/A") {
                        window.open(`tel:${request.provider.phone}`, "_self");
                      } else {
                        GlobalService.showToast("error", "Error", "No phone number available for this provider");
                      }
                    }}
                  >
                    <PhoneCall className="h-4 w-4" />
                    Contact
                  </Button>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm text-gray-500">Current Location</span>
                    <div className="flex items-center gap-2 mt-1">
                      <MapPin className="h-4 w-4 text-gray-400" />
                      <span>{request.provider.currentLocation}</span>
                    </div>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">ETA</span>
                    <div className="flex items-center gap-2 mt-1">
                      <Timer className="h-4 w-4 text-gray-400" />
                      <span>{request.provider.eta}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <h3 className="font-medium">Provider Assignment</h3>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-yellow-600" />
                    <span className="font-medium text-yellow-800">
                      {emergencyProfessionals.length} providers available nearby
                    </span>
                  </div>
                  <Button size="sm" onClick={() => onAssignProvider(request)}>
                    Assign Provider
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Activity Log */}
          <div className="space-y-4">
            <h3 className="font-medium">Activity Log</h3>
            <div className="space-y-4">
              {request.notes.map((note: any, index: number) => (
                <div key={index} className="flex gap-3">
                  <div className={`p-2 rounded-lg ${note.author === "System" ? "bg-gray-100" :
                      note.author === "Provider" ? "bg-blue-100" :
                        "bg-green-100"
                    }`}>
                    {note.author === "System" ? (
                      <Settings className="h-4 w-4 text-gray-600" />
                    ) : (
                      <User className="h-4 w-4 text-blue-600" />
                    )}
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">
                      {new Date(note.time).toLocaleString()}
                    </div>
                    <p className="mt-1">{note.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Admin Actions */}
          <div className="space-y-4 pt-4 border-t">
            <h3 className="font-medium">Emergency Actions</h3>
            <div className="flex flex-wrap gap-3">
              {!request.provider && (
                <Button className="flex items-center gap-2">
                  <UserCheck className="h-4 w-4" />
                  Assign Emergency Provider
                </Button>
              )}
              {request.provider && request.provider.whatsappNumber && (
                <Button 
                  variant="outline" 
                  className="flex items-center gap-2"
                  onClick={() => handleWhatsAppAssignment(request)}
                >
                  <PhoneCall className="h-4 w-4" />
                  Send WhatsApp Alert
                </Button>
              )}
              <Button 
                variant="outline" 
                className="flex items-center gap-2"
                onClick={() => {
                  if (request.customer.phone && request.customer.phone !== "N/A") {
                    window.open(`tel:${request.customer.phone}`, "_self");
                  } else {
                    GlobalService.showToast("error", "Error", "No phone number available for this customer");
                  }
                }}
              >
                <PhoneCall className="h-4 w-4" />
                Call Customer
              </Button>
              <Button variant="outline" className="flex items-center gap-2">
                <Bell className="h-4 w-4" />
                Send Emergency Alert
              </Button>
              <Button
                variant="outline"
                className="flex items-center gap-2"
                onClick={() => onEditJob(request)}
              >
                <Edit className="h-4 w-4" />
                Edit Emergency
              </Button>
              <Button variant="outline" className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="h-4 w-4" />
                Escalate Emergency
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestDetailsModal;
